import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import AuditActionBadge from './AuditActionBadge';

const AuditLogTable = ({ logs, loading }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString)?.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const toggleRow = (id) => setExpandedRow(prev => prev === id ? null : id);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <Icon name="Loader2" size={32} className="text-accent animate-spin" />
            <p className="text-sm text-muted-foreground">Loading audit logs...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!logs || logs?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Icon name="ClipboardList" size={40} className="text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">No audit logs found</p>
          <p className="text-xs text-muted-foreground">Try adjusting your filters or date range</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground">Timestamp</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground">Action</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground">Performed By</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground">Affected User</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground">Description</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-foreground">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {logs?.map((log) => (
              <React.Fragment key={log?.id}>
                <tr className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-xs text-foreground font-mono whitespace-nowrap">{formatDate(log?.created_at)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <AuditActionBadge actionType={log?.action_type} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {log?.performer?.full_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{log?.performer?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">
                        {log?.affected?.full_name || log?.affected_user_email || '—'}
                      </p>
                      {log?.affected?.email && (
                        <p className="text-xs text-muted-foreground truncate">{log?.affected?.email}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground max-w-xs truncate">{log?.description || '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {(log?.before_value || log?.after_value) && (
                      <button
                        onClick={() => toggleRow(log?.id)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      >
                        <Icon name={expandedRow === log?.id ? 'ChevronUp' : 'ChevronDown'} size={16} />
                      </button>
                    )}
                  </td>
                </tr>
                {expandedRow === log?.id && (log?.before_value || log?.after_value) && (
                  <tr className="bg-muted/20">
                    <td colSpan={6} className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {log?.before_value && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1">
                              <Icon name="Minus" size={12} /> Before
                            </p>
                            <pre className="text-xs text-red-800 whitespace-pre-wrap font-mono">
                              {JSON.stringify(log?.before_value, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log?.after_value && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                              <Icon name="Plus" size={12} /> After
                            </p>
                            <pre className="text-xs text-green-800 whitespace-pre-wrap font-mono">
                              {JSON.stringify(log?.after_value, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditLogTable;
